"use client"

import {
  AlertTriangle,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  LogOut,
  Package,
  PackageX,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import Pagination from "../../Common/Pagination"
import { getCloudinaryThumbnail } from "@/utils/urlUtils"

function SkeletonRows() {
  return Array.from({ length: 8 }).map((_, i) => (
    <tr key={i} className="animate-pulse border-b border-gray-100">
      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-8" /></td>
      <td className="px-4 py-4">
        <div className="h-4 bg-gray-200 rounded w-36 mb-1.5" />
        <div className="h-3 bg-gray-100 rounded w-20" />
      </td>
      <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-24" /></td>
      <td className="px-4 py-4">
        <div className="h-4 bg-gray-200 rounded w-28 mb-1.5" />
        <div className="h-1.5 w-16 bg-gray-200 rounded-full mt-2" />
        <div className="h-3 bg-gray-100 rounded w-24 mt-1.5" />
      </td>
      <td className="px-4 py-4"><div className="h-6 bg-gray-200 rounded-full w-20" /></td>
      <td className="hidden lg:table-cell px-4 py-4"><div className="h-4 bg-gray-200 rounded w-32" /></td>
      <td className="px-4 py-4">
        <div className="flex gap-2">
          <div className="h-8 bg-gray-200 rounded w-20" />
          <div className="h-8 bg-gray-200 rounded w-16" />
        </div>
      </td>
    </tr>
  ))
}

export default function InventoryManagementTable({
  activeTab,
  tableData,
  paginatedData,
  loading,
  searchTerm,
  showAddItem,
  openAddForm,
  showPurchase,
  showIssue,
  openPurchaseForm,
  openIssueForm,
  handleRowClick,
  sortColumn,
  sortDirection,
  handleInventorySort,
  getStatusBadgeClass,
  currentPage,
  currentTotal,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  isColorEnabled,
  isSubCategoryEnabled,
}) {
  const getSortIcon = (column) => {
    if (column !== sortColumn) return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />
    return sortDirection === "asc"
      ? <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
      : <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />
  }

  return (
    <>
      <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
        Showing key fields in list. Click any row for full item information.
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-y border-gray-200">
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleInventorySort("inventory_id")}
              >
                <div className="flex items-center">
                  ID
                  {getSortIcon("inventory_id")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleInventorySort("item_name")}
              >
                <div className="flex items-center">
                  Item Name
                  {getSortIcon("item_name")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleInventorySort("category")}
              >
                <div className="flex items-center">
                  {isSubCategoryEnabled ? "Category / Sub-category" : "Category"}
                  {getSortIcon("category")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleInventorySort("quantity_available")}
              >
                <div className="flex items-center">
                  Quantity
                  {getSortIcon("quantity_available")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleInventorySort("status")}
              >
                <div className="flex items-center">
                  Status
                  {getSortIcon("status")}
                </div>
              </th>
              <th
                className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleInventorySort("last_stock_update")}
              >
                <div className="flex items-center">
                  Last Updated
                  {getSortIcon("last_stock_update")}
                </div>
              </th>
              {activeTab === "nonmoving" && (
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Last Issued
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <div className="flex items-center">Actions</div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && paginatedData.length === 0 ? (
              <SkeletonRows />
            ) : tableData.length === 0 ? (
              <tr>
                <td colSpan={activeTab === "nonmoving" ? 8 : 7} className="p-0">
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <PackageX className="h-12 w-12 text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">
                      {activeTab === "reorder"
                        ? "No reorder alerts"
                        : activeTab === "nonmoving"
                          ? "No non-moving items"
                          : searchTerm
                            ? "No items found matching your search"
                            : "No inventory items yet"}
                    </p>
                    {!searchTerm && activeTab === "all" && showAddItem && (
                      <Button onClick={openAddForm} className="mt-3 bg-[#0263cc] hover:bg-[#0263cc]/90">
                        <Package className="h-4 w-4 mr-2" />
                        Add first item
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              paginatedData.map((item) => {
                const isLowStock = item.quantity_available <= item.minimum_stock_level
                const needsReorder = item.quantity_available <= item.reorder_level

                return (
                  <tr
                    key={item.inventory_id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleRowClick(item)}
                  >
                    <td className="px-4 py-4 text-sm text-gray-600">{item.inventory_id}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="relative h-10 w-10 flex-shrink-0">
                          {item.images?.[0] ? (
                            <img
                              src={getCloudinaryThumbnail(item.images[0])}
                              alt={item.item_name}
                              className="h-10 w-10 rounded-full object-cover border border-gray-200"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                              <Package className="h-4 w-4 text-gray-400" />
                            </div>
                          )}
                          {item.images?.length > 1 && (
                            <span className="absolute -bottom-1 -right-1 h-4.5 min-w-[1.125rem] px-1 rounded-full bg-[#0263cc] text-white text-[10px] font-semibold flex items-center justify-center border border-white">
                              +{item.images.length - 1}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="font-medium">{item.item_name}</div>
                          {item.size && <div className="text-xs sm:text-sm text-gray-600 mt-0.5">Size: {item.size}</div>}
                          {item.brand && <div className="text-xs sm:text-sm text-gray-600 mt-0.5">Brand: {item.brand}</div>}
                          {isColorEnabled && item.custom_attributes?.color && (
                            <div className="text-xs sm:text-sm text-gray-600 mt-0.5">Color: {item.custom_attributes.color}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {isSubCategoryEnabled && item.sub_category
                        ? `${item.category} / ${item.sub_category}`
                        : item.category}
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium">
                        Available: {item.quantity_available} {item.unit_of_measurement || ""}
                      </div>
                      <div className="text-xs text-amber-700 font-medium mt-0.5">
                        On Hold: {item.on_hold_quantity || 0}
                      </div>
                      <div className="mt-1 h-1.5 w-16 min-w-[4rem] rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            item.quantity_available <= 0
                              ? "bg-red-500"
                              : item.quantity_available <= (item.minimum_stock_level || 0)
                                ? "bg-red-400"
                                : item.quantity_available <= (item.reorder_level || 0)
                                  ? "bg-amber-500"
                                  : "bg-green-500"
                          }`}
                          style={{
                            width: `${
                              (item.reorder_level || 0) > 0
                                ? Math.min(100, (item.quantity_available / (item.reorder_level || 1)) * 100)
                                : 100
                            }%`,
                          }}
                        />
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        Min: {item.minimum_stock_level} | Reorder: {item.reorder_level}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`${getStatusBadgeClass(item.status)} whitespace-nowrap`}>{item.status}</span>
                    </td>
                    <td className="hidden lg:table-cell px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                      <div>{new Date(item.last_stock_update).toLocaleDateString()}</div>
                      <div>{new Date(item.last_stock_update).toLocaleTimeString()}</div>
                    </td>
                    {activeTab === "nonmoving" && (
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {item.last_issue_date
                          ? `${Math.floor((Date.now() - new Date(item.last_issue_date).getTime()) / (1000 * 60 * 60 * 24))} days ago`
                          : "Never"}
                      </td>
                    )}
                    <td
                      className="px-4 py-4"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TooltipProvider>
                        <div className="flex flex-wrap gap-2">
                          {showPurchase && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-green-700 border-green-300 hover:bg-green-50 px-2 h-8"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={() => openPurchaseForm(item)}
                                >
                                  <Package className="h-3 w-3 mr-1" />
                                  <span className="text-xs">Purchase</span>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">Stock In / Add purchase</TooltipContent>
                            </Tooltip>
                          )}
                          {showIssue && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-orange-700 border-orange-300 hover:bg-orange-50 px-2 h-8"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={() => openIssueForm(item)}
                                  disabled={!item.quantity_available || item.quantity_available <= 0}
                                >
                                  <LogOut className="h-3 w-3 mr-1" />
                                  <span className="text-xs">Issue</span>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">Issue stock</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TooltipProvider>
                      {isLowStock && (
                        <div className="flex items-center text-xs text-yellow-600 mt-1">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Low Stock
                        </div>
                      )}
                      {needsReorder && !isLowStock && (
                        <div className="flex items-center text-xs text-orange-600 mt-1">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Reorder Soon
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={currentTotal}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
        pageSizeOptions={[10, 25, 50, 100]}
        itemLabel="items"
      />
    </>
  )
}

